import type { ReactElement } from 'react';
import React, { useState } from 'react';
import { getYear } from 'date-fns';
import { FormGroup, Label } from 'reactstrap';
import Select from 'atom-components/select/select';
import { ascendingOrder } from 'helpers/tanstack-query.helpers';
import { useCustomerUser } from 'services/assist-api/entities/customer-user/customer-users';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import type { Me } from 'services/assist-api/entities/me/me';
import { useProducts } from 'services/assist-api/entities/product/products';
import type { Year } from 'services/assist-api/entities/test-instance-response/filters/year';
import { useResponseFilterYears } from 'services/assist-api/entities/test-instance-response/filters/year';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { useAuth } from 'services/providers/auth.provider';
import { isError, isInitialLoad } from 'utils/check-query-status';
import ProductCard from './product-card';
import { useTestInstanceUsers } from './queries';
import TestInstancesSkeleton from './skeleton';
import TestInstancesFallback from './test-instances-fallback';

function currentUserIsCustomerUser(
  currentUser: Me | undefined,
  customerUser: BaseCustomerUser | undefined
): boolean {
  return currentUser?.id === customerUser?.user?.id;
}

/**
 *TestInstancesByYear: UI to view test instances for a given customer user
 * - test instances for a given year
 *
 * **Intended use is as a child of** `AssignedTestInstances`
 * @param customerUserId id of the customer user
 */
export function TestInstancesByYear({
  year,
  customerUserId,
  isSchool
}: {
  year: string;
  customerUserId: number;
  isSchool?: boolean;
}): ReactElement {
  const products = useProducts({ order: ascendingOrder });

  const testInstanceUsers = useTestInstanceUsers(customerUserId, year);

  const currentUser = useAuth();
  const customerUser = useCustomerUser(customerUserId);

  if (isInitialLoad(products, testInstanceUsers, customerUser)) {
    return <TestInstancesSkeleton loading />;
  }
  if (isError(products, testInstanceUsers, customerUser)) {
    return <TestInstancesSkeleton error />;
  }
  if (!testInstanceUsers.data?.json.length) {
    return <TestInstancesFallback year={year} />;
  }

  const currentUserIsCustomerUserConst = currentUserIsCustomerUser(
    currentUser?.me,
    customerUser?.data?.json
  );

  return (
    <>
      {products.data?.json.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          isSchool={isSchool}
          testInstanceUsers={testInstanceUsers.data.json
            .filter((user) => product.areas.find((area) => area.id === user.testInstance.area.id))
            .sort((a, b) => (a.testInstance.startDate > b.testInstance.startDate ? 1 : -1))}
          currentUserIsCustomerUser={currentUserIsCustomerUserConst}
        />
      ))}
    </>
  );
}

export type AssignedTestInstancesProps = {
  customerUserId: number;
  isSchool?: boolean;
};

/**
 * Assigned TestInstances: UI to view test instances for a given customer user
 * - fetched by year (select menu to control year)
 * - grouped by product
 * - shows test instance response progress
 * - assessments (not surveys) link to report
 * - schools show terms, non-school date ranges
 * @param customerUserId id of the customer user
 */
export function AssignedTestInstances({
  customerUserId,
  isSchool
}: AssignedTestInstancesProps): ReactElement {
  // customerID of the school/organisation that you're logged in as
  const customerId = useAuthCustomer()?.id || 0;

  const years = useResponseFilterYears({ filters: { 'testInstance.customer': customerId } });

  const [year, setYear] = useState<Year | null>({
    id: getYear(new Date()),
    name: `${getYear(new Date())}`,
    value: `${getYear(new Date())}`
  });

  return (
    <>
      <div>
        <FormGroup>
          <Label htmlFor="year-select">Select a Year</Label>
          <Select<Year>
            inputId="year-select"
            options={years.data?.json || []}
            value={year}
            getOptionValue={(o): string => String(o.value)}
            getOptionLabel={(o): string => String(o.name)}
            onChange={(option): void => setYear(option)}
            isLoading={isInitialLoad(years)}
          />
        </FormGroup>
      </div>
      {year && (
        <TestInstancesByYear
          year={year.value}
          customerUserId={customerUserId}
          isSchool={isSchool}
        />
      )}
    </>
  );
}
